import React, { createContext, useContext, useReducer, useState, useEffect } from 'react'
import { useUser } from '@fs/zion-user'
import editInitialValues from '../pages/Avatar/state/initialValues'
import avatarReducer, { ACTIONS as AVATAR_ACTIONS } from '../pages/Avatar/state/avatarReducer'
import selectorReducer, {
  initialValues as selectorInitialValues,
  ACTIONS as SELECTOR_ACTIONS,
} from '../pages/Avatar/state/selectorReducer'
import { useGetAllAvatars, useSaveAvatar, useSetActiveAvatar, useDeleteAvatar } from '../pages/Avatar/hooks/api'
import useIsComponentMounted from '../hooks/useIsComponentMounted'

const AvatarsContext = createContext()
export const useAvatarsContext = () => useContext(AvatarsContext)

export default function AvatarsProvider({ children }) {
  const isComponentMountedRef = useIsComponentMounted()
  const user = useUser()
  const queryEnabled = !user?.userLoading && user?.signedIn
  const {
    query: { refetch: refetchAvatars },
    data: avatarsData,
  } = useGetAllAvatars(queryEnabled)

  const {
    mutation: { mutateAsync: saveAvatar, isSuccess: saveAvatarSuccess, data: savedAvatarData },
  } = useSaveAvatar()

  const {
    mutation: { mutateAsync: setActiveAvatar, isSuccess: setActiveSucceeded },
  } = useSetActiveAvatar()
  const {
    mutation: { mutateAsync: deleteAvatar, isSuccess: deleteSucceeded },
  } = useDeleteAvatar()

  const [myActiveAvatar, setMyActiveAvatar] = useState({})
  const [myFamilyActiveAvatars, setMyFamilyActiveAvatars] = useState([])
  const [avatarSelectorState, avatarSelectorDispatch] = useReducer(selectorReducer, selectorInitialValues)
  const [avatarEditState, avatarEditDispatch] = useReducer(avatarReducer, editInitialValues)

  const resetSelectorState = () => avatarSelectorDispatch({ actionType: SELECTOR_ACTIONS.RESET_STATE })
  const resetEditState = () => avatarEditDispatch({ actionType: AVATAR_ACTIONS.RESET_STATE })

  useEffect(() => {
    if ((deleteSucceeded || setActiveSucceeded || saveAvatarSuccess) && isComponentMountedRef.current) refetchAvatars()
  }, [deleteSucceeded, refetchAvatars, saveAvatarSuccess, setActiveSucceeded, isComponentMountedRef])

  useEffect(() => {
    if (!isComponentMountedRef.current) return
    if (avatarsData?.items?.length > 0) {
      const activeAvatars =
        avatarsData?.items?.filter((avatar) => avatar?.active && avatar?.forPersonId !== 'CURRENT') ?? []
      const [myActive] =
        avatarsData?.items?.filter((avatar) => avatar?.active && avatar?.forPersonId === 'CURRENT') ?? {}
      if (isComponentMountedRef.current) {
        setMyActiveAvatar(myActive)
        setMyFamilyActiveAvatars(activeAvatars)
      }
    }
  }, [avatarsData, isComponentMountedRef])

  useEffect(() => {
    const forPersonId = avatarSelectorState?.avatarSelectorPersonId
    if (forPersonId && saveAvatarSuccess && isComponentMountedRef.current) {
      setActiveAvatar({
        forPersonId,
        avatarId: savedAvatarData?.id ?? savedAvatarData?.avatarId,
      })
      resetEditState()
    }
  }, [
    saveAvatarSuccess,
    savedAvatarData,
    avatarSelectorState?.avatarSelectorPersonId,
    setActiveAvatar,
    isComponentMountedRef,
  ])

  useEffect(() => {
    if (avatarSelectorState?.avatarSelectorPersonId && isComponentMountedRef.current)
      // when the personId is set in the selector state, we filter all available avatars by personId
      avatarSelectorDispatch({
        actionType: SELECTOR_ACTIONS.SET_BY_PERSON_ID,
        value: { availableAvatars: avatarsData?.items ?? [] },
      })
  }, [avatarSelectorState?.avatarSelectorPersonId, avatarsData?.items])

  return (
    <AvatarsContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        myActiveAvatar,
        myFamilyActiveAvatars,
        avatarSelectorState,
        avatarSelectorDispatch,
        resetSelectorState,
        avatarEditState,
        avatarEditDispatch,
        resetEditState,
        refetchAvatars,
        saveAvatar,
        setActiveAvatar,
        deleteAvatar,
      }}
    >
      {children}
    </AvatarsContext.Provider>
  )
}
